/**
 * Tradução do corpo de erro padronizado do middleware.
 *
 * O contrato manda `{ codigo, message }`. **A tela decide pelo CÓDIGO, nunca
 * pela mensagem** — mensagem é texto para gente, muda de redação sem aviso e
 * não serve de condição. Por isso todo caminho de erro do painel chega aqui e
 * sai como `ErroApi`, que carrega o código estável.
 *
 * A mensagem do servidor só é exibida quando não temos tradução própria para
 * aquele código: assim um código novo da API não vira tela muda, e um payload
 * arbitrário (proxy fora do ar devolvendo HTML, stack trace vazado) nunca
 * chega ao supervisor.
 */

/** Texto exibido quando não há nada confiável a dizer sobre a falha. */
export const MENSAGEM_GENERICA = 'Não foi possível concluir a operação. Tente novamente.';

/** Além deste tamanho, a mensagem do servidor não é texto de tela — é despejo. */
const LIMITE_DA_MENSAGEM = 300;

/** Erro do contrato, já traduzido. `codigo` é o que a tela examina. */
export class ErroApi extends Error {
  override readonly name = 'ErroApi';

  constructor(
    readonly status: number,
    readonly codigo: string,
    mensagem: string,
    readonly caminho: string,
  ) {
    super(mensagem);
  }
}

/** Códigos que o painel deriva sozinho, sem o middleware ter mandado nenhum. */
const CODIGO_POR_STATUS: Readonly<Record<number, string>> = {
  400: 'REQUISICAO_INVALIDA',
  401: 'ACESSO_NAO_AUTENTICADO',
  403: 'PERFIL_INSUFICIENTE',
  404: 'NAO_ENCONTRADO',
  409: 'CONFLITO',
  422: 'REGRA_VIOLADA',
  429: 'LIMITE_DE_TENTATIVAS',
};

/** Status sem entrada acima (5xx inclusive) — o middleware não concluiu. */
const CODIGO_PADRAO = 'FALHA_NO_MIDDLEWARE';

/**
 * Redação do painel para cada código conhecido.
 *
 * Quem manda aqui é a nossa linguagem, não a do servidor: a API fala com
 * várias frentes, o painel fala com o supervisor.
 */
const TRADUCOES: Readonly<Record<string, string>> = {
  // Derivados do status
  REQUISICAO_INVALIDA: 'Os dados enviados não foram aceitos. Revise os campos e tente de novo.',
  ACESSO_NAO_AUTENTICADO: 'Sua sessão terminou. Entre novamente para continuar.',
  PERFIL_INSUFICIENTE: 'Seu perfil não permite esta ação.',
  NAO_ENCONTRADO: 'O registro não existe mais ou nunca existiu.',
  CONFLITO: 'Outra alteração conflitou com esta. Recarregue e tente de novo.',
  REGRA_VIOLADA: 'A operação não atende a uma regra do armazém.',
  LIMITE_DE_TENTATIVAS: 'Muitas tentativas seguidas. Espere um instante e tente de novo.',
  FALHA_NO_MIDDLEWARE: MENSAGEM_GENERICA,

  // Rede e contrato — nascem no próprio painel
  SEM_CONEXAO: 'O middleware não respondeu. Verifique a conexão e tente de novo.',
  SEM_RESPOSTA:
    'O middleware demorou demais para responder. A tela não vai esperar para sempre — tente de novo.',
  FALHA_NA_REQUISICAO: MENSAGEM_GENERICA,
  RESPOSTA_INVALIDA: 'O middleware respondeu fora do contrato. A tela não vai exibir dado incerto.',

  // Usuários e operadores (UC13)
  ULTIMO_ADMINISTRADOR:
    'Este é o último Administrador ativo. Promova outra pessoa antes de desativar ou rebaixar este acesso.',
  LOGIN_EM_USO: 'Este login já pertence a outra pessoa.',
  MATRICULA_EM_USO: 'Esta matrícula já pertence a outro operador.',
  SENHA_ATUAL_OBRIGATORIA: 'Para trocar a própria senha, informe a senha atual.',
  SENHA_ATUAL_INVALIDA: 'A senha atual não confere.',
  PERFIL_PROPRIO: 'Ninguém altera o próprio perfil nem se desativa.',
  PIN_INVALIDO:
    'PIN recusado: use de 4 a 8 dígitos, sem sequência crescente ou decrescente e sem repetir o mesmo dígito.',
  USUARIO_NAO_ENCONTRADO: 'Este usuário não existe mais.',
  OPERADOR_NAO_ENCONTRADO: 'Este operador não existe mais.',

  // Dispositivos (UC12)
  IDENTIFICADOR_EM_USO: 'Este identificador já pertence a outro dispositivo.',
  DISPOSITIVO_BLOQUEADO: 'O dispositivo está bloqueado e não fala com o middleware.',
  PAREAMENTO_DE_DISPOSITIVO_BLOQUEADO:
    'Não há como parear um dispositivo bloqueado. Desbloqueie antes de gerar o código.',
  DISPOSITIVO_NAO_ENCONTRADO: 'Este dispositivo não existe mais.',

  // Integração com o ERP (RF-089, RNF-073)
  INTEGRACAO_NAO_CONFIGURADA: 'Este armazém ainda não tem integração com ERP configurada.',
  INTEGRACAO_ERP_INATIVA:
    'A integração com o ERP está desligada. Ligue-a em Configurações antes de importar do ERP.',
  CREDENCIAL_ERP_AUSENTE:
    'Informe o usuário e a senha do ERP antes de ligar a integração — sem credencial, toda requisição ao Senior volta recusada.',
  CREDENCIAL_ERP_INVALIDA:
    'Credencial recusada. O usuário não aceita espaços, e nem usuário nem senha podem ficar em branco.',
  ENDPOINT_ERP_INVALIDO:
    'Endereço do middleware Senior recusado. Informe o endereço completo, começando por http ou https.',
  TRANSPORTE_ERP_INSEGURO:
    'No modo de autenticação 0 a senha do ERP viaja em claro dentro do XML: o endereço precisa ser https. Use https ou troque para o modo 2.',
  SIGLA_INTEGRACAO_INVALIDA:
    'Sigla recusada: até 15 caracteres, sem espaços. `WMW` é reservada pelo Senior — com ela o ERP devolve a lista vazia sem erro.',
  EMPRESA_ERP_INVALIDA: 'Código de empresa recusado: informe um inteiro entre 1 e 9999.',
  FILIAIS_ERP_INVALIDAS:
    'Lista de filiais recusada: informe ao menos uma, sem repetir, cada uma entre 1 e 99999.',
  MODO_AUTENTICACAO_INVALIDO: 'Modo de autenticação recusado: o Senior aceita 0 ou 2.',
  TEMPO_LIMITE_INVALIDO: 'Tempo limite recusado: informe entre 1.000 e 120.000 ms.',
  TAMANHO_LOTE_INVALIDO: 'Tamanho de lote recusado: informe entre 1 e 50.',
  COFRE_INDISPONIVEL:
    'O cofre de credenciais não respondeu. A configuração continua salva; tente de novo em instantes.',

  // Identificação por lote
  LOTES_NAO_SINCRONIZADOS:
    'Ainda não há lotes sincronizados do ERP. Carregue os lotes antes de identificar o armazém por lote.',
  CARGA_DE_LOTES_EM_ANDAMENTO: 'A carga de lotes já está rodando. Acompanhe o estado aqui.',
  CARGA_INICIAL_NAO_CONCLUIDA:
    'A carga de lotes só roda depois da carga inicial concluída. Conclua a carga inicial em Sincronização.',
  LOTE_NAO_CABE_NO_QR:
    'O código do lote não cabe no QR da etiqueta (até 14 caracteres quaisquer, 20 em maiúsculas e dígitos, ou 34 só dígitos).',

  // Movimentação, coleta e contagem por filial + depósito (UC07, UC08)
  DESTINO_OBRIGATORIO: 'Toda movimentação precisa de depósito de destino.',
  // Os códigos chegam do coletor, mas quem lê a mensagem é quem abre o painel —
  // e a próxima pergunta dele é sempre "onde eu conserto isso?".
  DEPOSITO_DESCONHECIDO:
    'O depósito não está na lista desta filial. Sincronize os depósitos do ERP em Depósitos.',
  DEPOSITO_INATIVO:
    'O depósito está inativo no ERP e não aceita operação. Reative-o no Senior e sincronize em Depósitos.',
  ORIGEM_IGUAL_AO_DESTINO: 'A origem e o destino são o mesmo depósito da mesma filial.',
  MOVIMENTACAO_ENTRE_FILIAIS:
    'Movimentação entre filiais não é aceita: origem e destino precisam ser da mesma filial.',
  DEPOSITO_BLOQUEADO: 'Há inventário bloqueante em andamento neste depósito.',
  DEPOSITO_EM_CONTAGEM: 'Outra pessoa já está contando este depósito.',
  RESERVA_NAO_ENCONTRADA: 'Esta reserva já foi liberada ou a contagem já terminou.',

  // Movimentação em duas etapas (r1-mov)
  ABERTURA_INEXISTENTE: 'Esta movimentação não existe neste armazém.',
  ABERTURA_SEM_DIVERGENCIA:
    'Esta divergência já foi decidida por outra pessoa, ou deixou de existir. Recarregue para ver o estado atual.',
  ABERTURA_NAO_CANCELAVEL:
    'Só se cancela o saldo de uma movimentação em andamento. Ela já foi finalizada, cancelada, ou está em divergência — nesse caso, decida a divergência antes.',
  ABERTURA_NAO_RECEBE_ENTREGA:
    'A movimentação já foi finalizada, cancelada ou está em divergência, e não recebe mais entregas.',

  // Divergência de saldo com o ERP (r1-sync)
  DIVERGENCIA_SALDO_INEXISTENTE: 'Esta divergência de saldo não existe neste armazém.',
  DIVERGENCIA_SALDO_JA_DECIDIDA:
    'Esta divergência de saldo já foi decidida por outra pessoa. Recarregue para ver o estado atual.',

  // Leitura por imagem no inventário (qr-code.md §6.1 e §9.7). O 400 do PUT
  // não traz código: quem o dá é `api/configuracao-matching.ts`.
  PARAMETRO_DE_LEITURA_FORA_DA_FAIXA:
    'Algum parâmetro da leitura por imagem ficou fora da faixa aceita, ou os limiares de confiança saíram de ordem (mínimo ≤ média ≤ aceite). Confira os valores em Avançado.',

  // Carga inicial da sincronização (r1-sync)
  CARGA_INICIAL_CONCLUIDA:
    'A carga inicial já foi concluída. Ela roda uma vez só por armazém; daqui em diante, quem atualiza é a sincronização periódica.',
  CARGA_INICIAL_EM_ANDAMENTO:
    'A carga inicial já está em andamento. Acompanhe o progresso nesta tela.',
};

/** Texto do servidor só passa se for uma frase — nem vazia, nem um despejo. */
function mensagemDoServidor(corpo: Record<string, unknown>): string | null {
  const bruta = corpo['message'];
  const texto = Array.isArray(bruta)
    ? bruta.filter((parte): parte is string => typeof parte === 'string').join(' ')
    : typeof bruta === 'string'
      ? bruta
      : '';

  const limpa = texto.trim();
  return limpa.length > 0 && limpa.length <= LIMITE_DA_MENSAGEM ? limpa : null;
}

async function corpoDaResposta(resposta: Response): Promise<Record<string, unknown>> {
  try {
    const corpo: unknown = await resposta.json();
    return typeof corpo === 'object' && corpo !== null ? (corpo as Record<string, unknown>) : {};
  } catch {
    // 502 em HTML, corpo vazio, proxy fora do ar: o detalhe técnico não
    // interessa a quem está na tela, mas o status ainda diz algo.
    return {};
  }
}

/**
 * Lê a resposta de erro e devolve o `ErroApi` correspondente.
 *
 * Nunca lança: uma falha ao interpretar a falha viraria erro sem código, e a
 * tela ficaria sem o que decidir.
 */
export async function erroDaResposta(resposta: Response, caminho: string): Promise<ErroApi> {
  const corpo = await corpoDaResposta(resposta);
  const codigoDoCorpo = typeof corpo['codigo'] === 'string' ? corpo['codigo'] : null;
  const codigo = codigoDoCorpo ?? CODIGO_POR_STATUS[resposta.status] ?? CODIGO_PADRAO;
  const traducao = TRADUCOES[codigo];

  // Código explícito e conhecido: manda a nossa redação. Sem isso, a mensagem
  // do servidor — e, se ela não servir, a tradução do status.
  const mensagem =
    codigoDoCorpo !== null && traducao !== undefined
      ? traducao
      : (mensagemDoServidor(corpo) ?? traducao ?? MENSAGEM_GENERICA);

  return new ErroApi(resposta.status, codigo, mensagem, caminho);
}

/**
 * Nome da falha, por leitura direta da propriedade.
 *
 * `DOMException` — o que o `fetch` lança ao ser cortado — não é `instanceof
 * Error` em todo ambiente, então a checagem é pelo formato, não pela classe.
 */
function nomeDaFalha(erro: unknown): string {
  if (typeof erro !== 'object' || erro === null || !('name' in erro)) {
    return '';
  }

  const nome: unknown = (erro as { readonly name: unknown }).name;
  return typeof nome === 'string' ? nome : '';
}

export function ehErroApi(erro: unknown): erro is ErroApi {
  return erro instanceof ErroApi;
}

/**
 * Código de qualquer falha, inclusive as que nunca chegaram ao servidor.
 *
 * `TypeError` é o que o `fetch` lança quando não há resposta nenhuma — DNS,
 * offline, middleware fora do ar. `TimeoutError`/`AbortError` são a
 * requisição cortada pelo tempo limite do cliente (`client.ts`).
 */
export function codigoDoErro(erro: unknown): string {
  if (ehErroApi(erro)) {
    return erro.codigo;
  }

  // Requisição interrompida pelo tempo limite do cliente (ou por quem saiu da
  // tela). Sem este caso, a espera de uma consulta pendurada não teria
  // desfecho nenhum e a tela ficaria carregando para sempre.
  const nome = nomeDaFalha(erro);

  if (nome === 'TimeoutError' || nome === 'AbortError') {
    return 'SEM_RESPOSTA';
  }

  return erro instanceof TypeError ? 'SEM_CONEXAO' : 'FALHA_NA_REQUISICAO';
}

/** A redação do painel para um código, para quem precisa dar o código sozinho. */
export function mensagemPorCodigo(codigo: string): string {
  return TRADUCOES[codigo] ?? MENSAGEM_GENERICA;
}

/** Texto pronto para a tela. Exceção arbitrária nunca vaza para o supervisor. */
export function mensagemDoErro(erro: unknown): string {
  if (ehErroApi(erro)) {
    return erro.message;
  }

  return TRADUCOES[codigoDoErro(erro)] ?? MENSAGEM_GENERICA;
}

/** Falha de contrato: a resposta veio, mas não é o que o painel sabe exibir. */
export function erroDeContrato(caminho: string, status = 200): ErroApi {
  return new ErroApi(
    status,
    'RESPOSTA_INVALIDA',
    TRADUCOES['RESPOSTA_INVALIDA'] ?? MENSAGEM_GENERICA,
    caminho,
  );
}
